import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import { SurveyAnswerForm } from '@/features/surveys/SurveyAnswerForm';
import { SurveyQuestionsPreview } from '@/features/surveys/SurveyQuestionsPreview';
import { SurveyResponsesList } from '@/features/surveys/SurveyResponsesList';
import { SurveySubmissionStatus } from '@/features/surveys/SurveySubmissionStatus';
import { SurveyResultsView } from '@/features/surveys/SurveyResultsView';
import {
  createEventSurvey,
  fetchEventSurveys,
  fetchSurveyTemplates,
  fetchSurveyVersions,
  type Question,
  type SurveyKind,
} from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso, formatDateTime } from '@/lib/datetime';

/**
 * イベントの「事前アンケート」「事前課題」「事後アンケート」「事後課題」タブ（指示書 §7-2・§12 M5 / D-128・D-188）。
 * `kinds` で扱う種類を絞る。割り当てのテンプレート候補も同じ種類だけに絞られる。
 *
 * 受講生の回答はモーダルにせず、各アンケート枠内にインライン展開する（D-334）。
 * 事務局・サクセスの提出状況・個別回答もタブ内インライン（D-314・D-340）。設問プレビューと集計だけモーダル。
 */
export function EventSurveysTab({
  eventId,
  eventTypeId,
  kinds,
  emptyText,
  enabled = true,
  onSetEnabled,
}: {
  eventId: string;
  /** このイベントの種別。割当候補のテンプレを絞る（D-286） */
  eventTypeId: string;
  kinds: SurveyKind[];
  emptyText: string;
  /** false=この回では使わない（D-222） */
  enabled?: boolean;
  onSetEnabled?: ((enabled: boolean) => void) | undefined;
}): ReactElement {
  const { user } = useAuth();
  const isOffice = user?.active_role === 'office';
  const isStudent = user?.active_role === 'student';
  const isDirector = user?.active_role === 'director';
  const isDirectorKind = kinds.includes('director');
  // 受講生向けは受講生、理事講師向けは理事・講師が回答する（D-349）
  const isRespondent = isStudent || (isDirector && isDirectorKind);
  // 個別回答（担当分）を確認できるロール。集計結果の可否は survey.can_view_results で
  // 1件ずつ判定する（テンプレートの result_scope に依存するため / §6・D-018）
  // 理事講師向けは事務局のみ提出状況を見る（サクセスには API 側で一覧を隠す）
  const canReview =
    isOffice || (user?.active_role === 'success' && !isDirectorKind);
  // 提出件数はAPI側で行スコープ済み。理事・講師には受講生向けでは常に空（0/0）が返るため列ごと出さない
  const showCounts = user?.active_role !== 'director';

  const query = useQuery({
    queryKey: ['event-surveys', eventId],
    queryFn: () => fetchEventSurveys(eventId),
  });

  /** 受講生・理事（理事向け）: 回答フォームを同じタブ内に展開（D-334・D-349） */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  /** 事務局・サクセス等: 確認用モーダル（D-094） */
  const [reviewId, setReviewId] = useState<string | null>(null);
  const expandRef = useRef<HTMLDivElement | null>(null);

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;

  // このタブで扱う種類のものだけを出す（事前と事後を混ぜない / D-128）
  const surveys = query.data.filter((survey) => kinds.includes(survey.template_kind));
  const reviewSurvey = surveys.find((s) => s.id === reviewId) ?? null;

  function toggleRespondentSurvey(surveyId: string): void {
    if (expandedId === surveyId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(surveyId);
    requestAnimationFrame(() => {
      expandRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {onSetEnabled && (
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onSetEnabled(e.target.checked)}
          />
          <span>
            この回で使う
            <span className="ml-2 text-wptext-muted">
              外すと未割当でも「次にやること」は済み扱いになります。
            </span>
          </span>
        </label>
      )}
      {!enabled && (
        <p className="text-wptext-muted">この回では使いません。</p>
      )}
      {isOffice && enabled && (
        <AssignSurveyForm eventId={eventId} eventTypeId={eventTypeId} kinds={kinds} />
      )}

      {enabled && surveys.length === 0 && <p className="text-wptext-muted">{emptyText}</p>}

      <div className="flex flex-col gap-4">
        {surveys.map((survey) => {
          // 開いたときに見せられる中身が何も無いなら、開閉UI自体を出さない
          const expandable = isRespondent || canReview || survey.can_view_results;
          const isExpanded = isRespondent && expandedId === survey.id;
          // 回答者には版番号・全体の提出件数は出さず、自分の状態だけを見せる（D-083）。
          // 件数は API 側で行スコープ済みなので、回答者の値は自分の分だけになる
          const ownState = !isRespondent
            ? null
            : survey.response_count === 0
              ? { label: '未回答', className: 'bg-wpnotice-warning/20 text-wptext-strong' }
              : survey.submitted_count > 0
                ? { label: '提出済み', className: 'bg-wpnotice-success/20 text-wptext-strong' }
                : { label: '下書き保存中', className: 'bg-wpcanvas text-wptext-strong' };
          const header = (
            <>
              <span className="font-semibold text-wptext-strong">
                {survey.template_name}
                {!isRespondent && `（v${survey.version}）`}
                {ownState && (
                  <span className={`ml-2 rounded-sm px-2 py-0.5 text-xs ${ownState.className}`}>
                    {ownState.label}
                  </span>
                )}
              </span>
              <span className="text-wptext-muted">
                {survey.deadline_at ? `締切 ${formatDateTime(survey.deadline_at)}` : '締切なし'}
                {showCounts && !isRespondent && ` ・ ${survey.submitted_count}/${survey.response_count}件 提出`}
              </span>
            </>
          );

          return (
            <div
              key={survey.id}
              ref={isExpanded ? expandRef : undefined}
              className={`rounded-sm border bg-white p-4 ${
                isExpanded ? 'border-wpaccent shadow-sm' : 'border-wpborder'
              }`}
            >
              {expandable ? (
                <>
                  <div className="flex w-full flex-wrap items-center justify-between gap-2">
                    {header}
                  </div>
                  {isRespondent ? (
                    <>
                      <button
                        type="button"
                        onClick={() => toggleRespondentSurvey(survey.id)}
                        className="mt-3 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
                        aria-expanded={isExpanded}
                      >
                        {isExpanded
                          ? '回答フォームを閉じる'
                          : survey.response_count === 0
                            ? 'ここから回答する'
                            : '回答を続ける'}
                      </button>
                      {isExpanded && (
                        <div className="mt-4 border-t border-wpborder pt-4">
                          <SurveyAnswerForm
                            eventSurveyId={survey.id}
                            questions={survey.questions}
                            deadlineAt={survey.deadline_at}
                          />
                          {survey.can_view_results && isStudent && (
                            <div className="mt-6 border-t border-wpborder pt-4">
                              <p className="mb-2 font-semibold text-wptext-strong">参加者の提出</p>
                              <SurveyResponsesList
                                eventSurveyId={survey.id}
                                eventId={eventId}
                                questions={survey.questions}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setReviewId(survey.id)}
                        className="mt-2 rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
                      >
                        {canReview
                          ? survey.can_view_results
                            ? '設問・集計を見る'
                            : '設問を見る'
                          : '内容を開く'}
                      </button>
                      {canReview && (
                        <>
                          <div className="mt-3 border-t border-wpborder pt-3">
                            <p className="mb-2 font-semibold text-wptext-strong">提出状況</p>
                            <SurveySubmissionStatus
                              eventId={eventId}
                              eventSurveyId={survey.id}
                              canSeeTargets={isOffice}
                              respondentRole={isDirectorKind ? 'director' : 'member'}
                            />
                          </div>
                          <div className="mt-4 border-t border-wpborder pt-4">
                            <p className="mb-2 font-semibold text-wptext-strong">個別回答</p>
                            <SurveyResponsesList
                              eventSurveyId={survey.id}
                              eventId={eventId}
                              questions={survey.questions}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="flex w-full items-center justify-between">{header}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* 事務局・サクセス・理事の確認はモーダル（一覧が長いときの確認用 / D-094） */}
      <Modal
        open={reviewSurvey !== null}
        title={reviewSurvey?.template_name ?? ''}
        onClose={() => setReviewId(null)}
        wide
      >
        {reviewSurvey && (
          <>
            <div className="mb-3">
              <SurveyQuestionsPreview questions={reviewSurvey.questions} />
            </div>
            {canReview ? (
              reviewSurvey.can_view_results && (
                <div className="mt-4">
                  <p className="mb-2 font-semibold text-wptext-strong">集計結果</p>
                  <SurveyResultsView eventSurveyId={reviewSurvey.id} />
                </div>
              )
            ) : (user?.active_role === 'director' && reviewSurvey.can_view_results) ? (
              <ReviewPanel
                eventSurveyId={reviewSurvey.id}
                eventId={eventId}
                canViewResults={reviewSurvey.can_view_results}
                questions={reviewSurvey.questions}
              />
            ) : (
              reviewSurvey.can_view_results && (
                <SurveyResultsView eventSurveyId={reviewSurvey.id} />
              )
            )}
          </>
        )}
      </Modal>
    </div>
  );
}

function ReviewPanel({
  eventSurveyId,
  eventId,
  canViewResults,
  questions,
}: {
  eventSurveyId: string;
  eventId: string;
  canViewResults: boolean;
  questions: Question[];
}): ReactElement {
  const [tab, setTab] = useState<'responses' | 'results'>('responses');

  // 集計結果を見られないロール（サクセス等）にはタブ切替を出さず、個別回答のみ表示する
  if (!canViewResults) {
    return (
      <SurveyResponsesList
        eventSurveyId={eventSurveyId}
        eventId={eventId}
        questions={questions}
      />
    );
  }

  return (
    <div>
      <div className="mb-3 flex gap-2">
        <button
          type="button"
          onClick={() => setTab('responses')}
          className={
            tab === 'responses' ? 'rounded-sm border border-wpaccent bg-wpaccent/10 px-3 py-1' : 'btn-secondary py-1'
          }
        >
          個別回答
        </button>
        <button
          type="button"
          onClick={() => setTab('results')}
          className={
            tab === 'results' ? 'rounded-sm border border-wpaccent bg-wpaccent/10 px-3 py-1' : 'btn-secondary py-1'
          }
        >
          集計結果
        </button>
      </div>
      {tab === 'responses' ? (
        <SurveyResponsesList
          eventSurveyId={eventSurveyId}
          eventId={eventId}
          questions={questions}
        />
      ) : (
        <SurveyResultsView eventSurveyId={eventSurveyId} />
      )}
    </div>
  );
}

function AssignSurveyForm({
  eventId,
  eventTypeId,
  kinds,
}: {
  eventId: string;
  eventTypeId: string;
  kinds: SurveyKind[];
}): ReactElement {
  const queryClient = useQueryClient();
  const templatesQuery = useQuery({
    queryKey: ['survey-templates', eventTypeId],
    queryFn: () => fetchSurveyTemplates(eventTypeId),
  });
  const [templateId, setTemplateId] = useState('');
  const [deadlineAt, setDeadlineAt] = useState('');
  const [lateAllowed, setLateAllowed] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // 版を公開済みで、かつこのタブの種類＋このイベント種別のテンプレートだけ（D-128・D-286）
  const publishable = (templatesQuery.data ?? []).filter(
    (t) => t.published_version !== null && kinds.includes(t.kind),
  );

  async function onAssign(): Promise<void> {
    if (!templateId) return;
    setBusy(true);
    setError(null);
    try {
      const versions = await fetchSurveyVersions(templateId);
      const published = versions.filter((v) => v.published_at !== null).sort((a, b) => b.version - a.version)[0];
      if (!published) {
        setError('公開済みの版がありません。');
        return;
      }
      await createEventSurvey(eventId, {
        template_version_id: published.id,
        deadline_at: deadlineAt ? datetimeLocalToIso(deadlineAt) : null,
        late_allowed: lateAllowed,
      });
      await queryClient.invalidateQueries({ queryKey: ['event-surveys', eventId] });
      setTemplateId('');
      setDeadlineAt('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '割当に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-sm border border-wpborder bg-white p-3">
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void onAssign();
        }}
      >
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">テンプレート</span>
          <select
            required
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">選択してください</option>
            {publishable.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}（v{t.published_version}）
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">締切（任意）</span>
          <input
            type="datetime-local"
            value={deadlineAt}
            onChange={(e) => setDeadlineAt(e.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="flex items-center gap-1 pb-1.5">
          <input
            type="checkbox"
            checked={lateAllowed}
            onChange={(e) => setLateAllowed(e.target.checked)}
          />
          期限後も受付ける
        </label>
        <button
          type="submit"
          disabled={busy}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          割り当てる
        </button>
      </form>
    </div>
  );
}
